import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Row, Col, Table, Drawer } from 'antd';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isEmail from 'validator/lib/isEmail';

import { pageChanged } from '../../../../../data/redux/page_details/actions';
import * as agentActions from '../../../../../data/redux/member_details/actions';

import { APP_PAGES, APP_LAYOUT, ROUTE_PATH } from '../../../../../data/config/constants';
import { uniqueId, showMessage, scrollToBottom, filterArrayBySearchText } from '../../../../../data/config/utils';
import { IconSuccess, IconHistory } from '../../../../../data/assets/icons';
import { IMG_ERROR } from '../../../../../data/assets/img';

import PageHeaderLoader from '../../../../../components/pageheader/loaders';
import CardRowTableLoader from '../../../components/loaders/cardrowtable';
import AvatarCardLoader from '../../../../../components/avatarcard/loader';
import CustomHeader from '../../../../../components/customheader';
import ProfileAvatar from '../../../../../components/profileavatar';
import ModalFooter from '../../../../../components/modalfooter';
import AgentsInvitationForm from './components/agentsinvitationform';
import AgentProfileCard from './components/agentprofilecard';
import TableCellAction from '../../../components/tablecellaction';
import ShowMessage from '../../../../../components/showmessage';

const roles = [
  { name: "Admin", value: "Admin" },
  { name: "Agent", value: "Agent" },
  { name: "Supervisor", value: "Supervisor" },
  { name: "QA", value: "QA" },
  { name: "Trainer", value: "Trainer" },
  { name: "Manager", value: "Manager" },
  { name: "Assistant Manager", value: "AssistantManager" },
  { name: "File Upload Agent", value: "FileUploadAgent" }
];

class Agents extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      filtered_agents: props.member_details.agents,
      search_text: "",
      new_agents: [],
      drawer_data: {
        title: "",
        show_invite_agent: false,
      },
    };
    this.columns = this.getAgentsTableColumns();
  }

  componentDidMount() {
    const { actions } = this.props;
    actions.pageChanged(APP_PAGES.AGENTS, APP_PAGES.MEMBERS);
    actions.getAllAgents();
    actions.getAllGroups();
  }

  componentWillReceiveProps(nextProps) {
    const { member_details } = this.props;
    const { search_text } = this.state;

    if (!isEqual(nextProps.member_details.agents, member_details.agents)) {
      const agents = [...nextProps.member_details.agents];
      if (search_text.trim().length !== 0) {
        this.setState({
          filtered_agents: filterArrayBySearchText(search_text, agents, 'firstName')
        });
      } else {
        this.setState({
          filtered_agents: agents
        });
      }
    }
  }

  getAgentsTableColumns = () => {
    const columns = [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        className: 'ori-l-pad-10',
        render: (text, record) => {
          return (
            <ProfileAvatar shape="square" container_class="ori-flex-ac" title={`${record.firstName} ${record.lastName}`} title_class="ori-font-sm ori-font-bold" default_avatar_bg={record.isDeleted} badge_dot={record.isOnline} />
          );
        }
      },
      {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
      },
      {
        title: 'Role',
        dataIndex: 'role',
        key: 'role',
        render: role => {
          if (role) {
            return (
              <span className={classNames("ori-lr-pad-10 ori-tb-pad-3 ori-border-radius-20 ori-font-white ori-bg-primary ori-font-11 ori-capitalize", { "ori-bg-danger": role === 'owner', "ori-bg-green": role === 'admin' })}>{role}</span>
            );
          }
          return null;
        }
      },
      {
        title: 'Status',
        dataIndex: 'isVerified',
        key: 'isVerified',
        align: 'center',
        render: (text, record) => {
          if (record.isDeleted) {
            return (
              <p className="ori-font-danger ori-text-center"><IconSuccess size={15} /> <span className="ori-lr-pad-3">DELETED</span></p>
            )
          } else if (record.isVerified) {
            return (
              <p className="ori-font-green ori-text-center"><IconSuccess size={15} /> <span className="ori-lr-pad-3">VERIFIED</span></p>
            )
          } else {
            return (
              <p className="ori-font-light ori-text-center"><IconHistory size={15} /> <span className="ori-lr-pad-3">PENDING</span></p>
            )
          }
        }
      },
      {
        title: 'Action',
        dataIndex: 'action',
        key: 'action',
        align: 'center',
        render: (text, record) => {
          return (
            <TableCellAction align_center show_user show_edit show_delete delete_popconfirm_title="Are you sure delete this agent?" handleUserClick={this.props.actions.updateMemberState.bind(this, record, 'active_agent')} handleEditClick={this.editAgentProfile.bind(this, record)} handleDeleteClick={this.deleteAgent.bind(this, record)} />
          );
        }
      },
    ];
    return columns;
  };

  handleAgentsSearch = event => {
    const search_text = event.target.value;
    const { member_details } = this.props;
    this.setState({ search_text });
    if (search_text.trim().length !== 0) {
      this.setState({
        filtered_agents: filterArrayBySearchText(search_text, member_details.agents, 'firstName')
      });
    } else {
      this.setState({
        filtered_agents: member_details.agents
      });
    }
  };

  setAgentInvitationFormContainerRef = el => {
    this.agentsInvitationFormContainerRef = el;
  };

  showInviteAgent = () => {
    this.setState({
      drawer_data: {
        title: "Invite New Agents",
        show_invite_agent: true,
      }
    });
  };

  closeDrawer = () => {
    this.setState({
      new_agents: [],
      drawer_data: {
        title: "",
        show_invite_agent: false,
      }
    });
  };

  onChangeNewAgentsData = (key, value, index) => {
    this.setState({
      new_agents: [
        ...this.state.new_agents.slice(0, index),
        {
          ...this.state.new_agents[index],
          [key]: value,
        },
        ...this.state.new_agents.slice(index + 1)
      ]
    });
  };

  onDeleteNewAgent = index => {
    if (index >= 0) {
      this.setState({
        new_agents: [
          ...this.state.new_agents.slice(0, index),
          ...this.state.new_agents.slice(index + 1)
        ]
      });
    }
  };

  addMoreNewAgent = () => {
    this.setState({
      new_agents: [
        ...this.state.new_agents,
        {
          group: ["General"],
          invitationWith: 'email'
        }
      ]
    }, () => {
      scrollToBottom(this.agentsInvitationFormContainerRef);
    });
  };

  inviteNewAgents = () => {
    const { new_agents } = this.state;
    const { actions } = this.props;
    let message = 'please fill all the required field of each agent';
    const index = new_agents.findIndex(agent => {
      agent.isEmail = (agent.invitationWith == 'email') ? true : false;
      delete agent.invitationWith;
      if (agent.isEmail) {
        if (agent.email && agent.firstName && agent.lastName && agent.role && agent.email.trim().length > 0 && agent.firstName.trim().length > 0 && agent.lastName.trim().length > 0) {
          if (!isEmail(agent.email)) {
            message = `please enter valid email id for ${agent.firstName}`;
            return true
          }
          return false;
        } else {
          return true;
        }
      } else {
        if (agent.userName && agent.firstName && agent.lastName && agent.role && agent.password && agent.userName.trim().length > 0 && agent.firstName.trim().length > 0 && agent.lastName.trim().length > 0 && agent.password.trim().length > 0) {
          if (agent.password != agent.confirmPassword) {
            message = `Confirm password must be same as password for ${agent.firstName}`;
            return true
          }
          agent.email = agent.userName;
          delete agent.userName;
          delete agent.confirmPassword;
          return false;
        } else {
          return true;
        }
      }
    });
    if (index === -1) {
      const payload = {
        users: new_agents,
        host: window.location.origin
      };
      actions.inviteNewAgents(payload, this.closeDrawer);
    } else {
      showMessage('warning', message);
    }
  };

  onTableRow = record => {
    const { member_details } = this.props;
    return {
      className: member_details.active_agent.email === record.email ? "ori-bg-default ori-l-border-primary-4" : "ori-bg-white ori-l-border-transparent-4",
    };
  };

  editAgentProfile = agent => {
    const { history } = this.props;
    if (agent && agent.email) {
      history.push(`${ROUTE_PATH.EDIT_AGENT_PROFILE}?email=${agent.email}`);
    } else {
      showMessage('error', 'require data is missing');
    }
  };

  deleteAgent = agent => {
    const { admin_id, actions } = this.props;
    const is_other_account = agent.psid && agent.psid !== admin_id;
    if (is_other_account) {
      if (agent && agent.email && agent._id) {
        const payload = {
          id: agent._id,
          email: agent.email,
        };
        actions.deleteAgent(payload);
      } else {
        showMessage('warning', 'user email/id is missing.');
      }
    } else {
      showMessage('warning', "you can not delete your account yourself.");
    }
  };

  renderAgentProfileCard = () => {
    const { member_details } = this.props;
    if (!isEmpty(member_details.active_agent)) {
      return (
        <AgentProfileCard agent={member_details.active_agent} onClickEdit={this.editAgentProfile} />
      );
    }
  };
  getAgentsWithcsv = new_agents => {
    this.setState({
      new_agents: [...new_agents]
    });
  }

  render() {
    const { screen_width, screen_height, member_details } = this.props;
    const { drawer_data, new_agents, filtered_agents } = this.state;

    if (member_details.loaders.agents_loading && !member_details.loaders.agents_loaded) {
      return (
        <div className="ori-lr-pad-10">
          <Row gutter={20}>
            <Col xs={24} sm={16} md={18}>
              <PageHeaderLoader bottom_bordered />
              <div className="ori-tb-pad-10">
                <CardRowTableLoader />
              </div>
            </Col>
            <Col xs={24} sm={8} md={6} className="ori-t-pad-20 ori-b-pad-15 ori-l-pad-5 ori-overflow-x-hidden ori-overflow-y-auto" style={{ height: `${screen_height - APP_LAYOUT.APP_HEADER_HEIGHT - APP_LAYOUT.PAGE_NAVBAR_HEIGHT}px` }}>
              <AvatarCardLoader />
            </Col>
          </Row>
        </div>
      )
    } else if (!member_details.loaders.agents_loading && member_details.loaders.agents_loaded) {
      return (
        <div className="ori-lr-pad-10">
          <Row gutter={20}>
            <Col xs={24} sm={16} md={18}>
              <CustomHeader title={`Agents (${filtered_agents.length})`} search_placeholder="Search agents" first_button_type="primary" first_button_text="+ Invite Agents" onClickFirstButton={this.showInviteAgent} onChangeSearch={this.handleAgentsSearch} show_search bottom_bordered show_first_button />
              <Table className="ori-table-default ori-table-header-bg-transparent" rowClassName="ori-cursor-ptr" scroll={{ x: 450 }} size={screen_width < 768 ? "small" : "middle"} rowKey={uniqueId} onRow={this.onTableRow} columns={this.columns} pagination={{ pageSize: 9 }} dataSource={filtered_agents} />
            </Col>
            <Col xs={24} sm={8} md={6} className="ori-t-pad-20 ori-b-pad-15 ori-l-pad-5 ori-overflow-x-hidden ori-overflow-y-auto" style={{ height: `${screen_height - APP_LAYOUT.APP_HEADER_HEIGHT - APP_LAYOUT.PAGE_NAVBAR_HEIGHT}px` }}>
              {this.renderAgentProfileCard()}
            </Col>
          </Row>
          <Drawer className="ori-drawer ori-drawer-title-primary ori-drawer-footer-pad ori-drawer-height-without-footer" width={screen_width < 768 ? screen_width : 700} title={drawer_data.title} visible={drawer_data.show_invite_agent} placement="right" maskClosable={false} onClose={this.closeDrawer}>
            {
              drawer_data.show_invite_agent &&
              <div className="ori-full-parent-height ori-overflow-y-auto ori-overflow-x-hidden" ref={this.setAgentInvitationFormContainerRef}>
                <AgentsInvitationForm new_agents={new_agents} role_options={roles} group_options={member_details.groups} onChange={this.onChangeNewAgentsData} onConfirmDeleteAgent={this.onDeleteNewAgent} onClickAddMoreAgent={this.addMoreNewAgent} getAgentsWithcsv={this.getAgentsWithcsv} />
                {
                  new_agents.length > 0 &&
                  <div className="ori-absolute ori-full-width ori-align-bottom ori-align-left ori-b-pad-5">
                    <ModalFooter button_ok_text={member_details.loaders.new_agent_adding ? "inviting" : "invite"} loading={member_details.loaders.new_agent_adding} handleCancelClick={this.closeDrawer} handleOkClick={this.inviteNewAgents} capsule_button />
                  </div>
                }
              </div>
            }
          </Drawer>
        </div>
      )
    } else if (!member_details.loaders.agents_loading && !member_details.loaders.agents_loaded) {
      return (
        <ShowMessage img_src={IMG_ERROR} title="Error" message="unable to load admin list. please check your internet connection and reload the page." height={150} width={150} />
      )
    } else {
      return null;
    }
  }
}

const mapStateToProps = state => {
  return {
    screen_width: state.page_details.device_data.screen_width,
    screen_height: state.page_details.device_data.screen_height,
    admin_id: state.admin_details.admin_id,
    member_details: state.member_details,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    actions: bindActionCreators(Object.assign({}, { pageChanged }, agentActions), dispatch)
  };
};

Agents.propTypes = {
  screen_width: PropTypes.number,
  screen_height: PropTypes.number,
  admin_id: PropTypes.string,
  member_details: PropTypes.object,
  actions: PropTypes.object,
  history: PropTypes.object,
  match: PropTypes.object,
};

export default connect(mapStateToProps, mapDispatchToProps, null, { withRef: true })(Agents);



// WEBPACK FOOTER //
// ./src/app/pagecontainer/modules/members/agents/index.js